import { ASTCall } from '@watershed/lscript/ast';
import {
  ExprType,
  NullableType,
  NumberType,
} from '@watershed/lscript/expr_types';
import { extractStringLiteral } from '../literal';
import {
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';
import { parseUnitString } from '@watershed/lscript/unit';
import { convertToType } from '../typeConversion';

// convert(expr, "unit name")
export const convert: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2, 2);
    assertKwargShape(call, [], false, warnings, undefined);

    const params = convertToTypeParamsFromCall(ctx, call, warnings);
    convertToType(ctx, params);
    return params.toType;
  },

  eval(ctx: Context, call: ASTCall): unknown {
    return convertToType(ctx, convertToTypeParamsFromCall(ctx, call)).eval();
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    return convertToType(ctx, convertToTypeParamsFromCall(ctx, call)).toSql();
  },
};

// This function is abstracted out so the implementation doesn't drift across
// `typecheck`, `eval`, and `toSql`.
function convertToTypeParamsFromCall(
  ctx: Context,
  call: ASTCall,
  warnings?: Array<TypecheckWarning>
): Parameters<typeof convertToType>[1] {
  const fromType = typecheckExpression(ctx, call.args[0], warnings);
  const toUnit = parseUnitString(extractStringLiteral(call.args[1]));
  const baseToType: NumberType = { ty: 'number', unit: toUnit };
  const toType: NumberType | NullableType =
    fromType.ty === 'nullable'
      ? { ty: 'nullable', baseTy: baseToType }
      : baseToType;
  return { fromExpr: call.args[0], toType };
}
